import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { api } from '../utils/api';

const TAB_LABELS = {
  overview: 'Overview',
  transactions: 'Transactions',
  subscriptions: 'Subscriptions',
  magi: 'MAGI',
  investments: 'Investments',
  ai: 'AI',
  manage: 'Manage',
  accounts: 'Accounts',
  admin: 'Admin',
};

export default function AdminStats() {
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.getAdminStats();
      setStats(data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!stats) {
    return <div style={styles.panel}><p style={styles.empty}>Loading stats...</p></div>;
  }

  const tabChartData = stats.tabPopularity.map((t) => ({
    name: TAB_LABELS[t.tab] || t.tab,
    views: t.views,
  }));

  const signupChartData = stats.signupsOverTime.map((s) => ({
    day: s.day.slice(5), // MM-DD
    signups: s.count,
  }));

  return (
    <div style={styles.wrap}>
      <div style={styles.statGrid}>
        <StatCard label="Households" value={stats.totalHouseholds} />
        <StatCard label="Total Users" value={stats.totalUsers} />
        <StatCard label="Accounts Connected" value={stats.accountsConnected} />
        <StatCard label="Active (7d)" value={stats.activeUsers7d} color="#00d4aa" />
        <StatCard label="Active (30d)" value={stats.activeUsers30d} color="#fbbf24" />
      </div>

      <div style={styles.panel}>
        <h2 style={styles.title}>📈 Signups - Last 30 Days</h2>
        {signupChartData.length === 0 ? (
          <p style={styles.empty}>No new signups in the last 30 days.</p>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={signupChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e2a3a" />
              <XAxis dataKey="day" tick={{ fill: '#6b7280', fontSize: 10, fontFamily: "'DM Mono', monospace" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: '#6b7280', fontSize: 10, fontFamily: "'DM Mono', monospace" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: '#1e2a3a', border: '1px solid #374151', borderRadius: '8px' }} labelStyle={{ color: '#9ca3af' }} />
              <Line type="monotone" dataKey="signups" stroke="#00d4aa" strokeWidth={2} dot={{ fill: '#00d4aa', r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div style={styles.panel}>
        <h2 style={styles.title}>🗂 Most-Visited Tabs - Last 30 Days</h2>
        {tabChartData.length === 0 ? (
          <p style={styles.empty}>No tab activity recorded yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(200, tabChartData.length * 40)}>
            <BarChart data={tabChartData} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e2a3a" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fill: '#6b7280', fontSize: 10, fontFamily: "'DM Mono', monospace" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11, fontFamily: "'Syne', sans-serif" }} axisLine={false} tickLine={false} width={90} />
              <Tooltip contentStyle={{ background: '#1e2a3a', border: '1px solid #374151', borderRadius: '8px' }} labelStyle={{ color: '#9ca3af' }} />
              <Bar dataKey="views" fill="#00d4aa" radius={[0, 4, 4, 0]} maxBarSize={20} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <p style={styles.disclaimer}>
        First-party stats collected directly by Boring Budgeting - not GA4.
        "Active" means at least one recorded login or tab view in the window.
      </p>
    </div>
  );
}

function StatCard({ label, value, color = '#e5e7eb' }) {
  return (
    <div style={styles.statCard}>
      <div style={{ ...styles.statValue, color }}>{value}</div>
      <div style={styles.statLabel}>{label}</div>
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' },
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: '0.75rem',
  },
  statCard: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '12px',
    padding: '1rem',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '1.6rem',
    fontWeight: '800',
    fontFamily: "'DM Mono', monospace",
  },
  statLabel: {
    color: '#6b7280',
    fontSize: '0.7rem',
    marginTop: '4px',
    fontFamily: "'Syne', sans-serif",
  },
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
  },
  title: {
    margin: '0 0 1rem',
    fontSize: '1rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  empty: { color: '#4b5563', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' },
  disclaimer: {
    color: '#4b5563',
    fontSize: '0.7rem',
    textAlign: 'center',
  },
};
